/**
 * Singles Store — Preview Lock Badge + Buy Track affordance.
 * =========================================================
 *
 * Reusable UI treatment for tracks that the backend has flagged with
 * `singles_locked=true` (unauthorized viewer of an actively-listed
 * Singles Store item). Consumers pass the raw track/submission object
 * emitted by any of the public read endpoints — this component pulls
 * `singles_for_sale`, `singles_locked`, `singles_price_usd`,
 * `singles_listing_id`, and `singles_buy_url` off the object.
 *
 * Purchase flow: `singles_buy_url` from the backend (typically
 * `/singles/{listing_id}`). We do NOT build a second checkout. This
 * badge only routes the user to the existing store detail page which
 * already owns the Stripe flow.
 *
 * Variants:
 *   - `variant="row"`   — inline pill for track lists (Artist Profile).
 *   - `variant="hero"`  — larger stacked block for Track Detail hero.
 *   - `variant="card"`  — bottom-corner overlay for cover-art cards.
 */
import { Link } from "react-router-dom";

const fmtUsd = (n) => {
  const x = Number(n || 0);
  if (!isFinite(x) || x <= 0) return "";
  return `$${x.toFixed(2)}`;
};

const resolveBuyPath = (track) => {
  if (!track) return null;
  if (track.singles_buy_url) return track.singles_buy_url;
  if (track.singles_listing_id) return `/singles/${track.singles_listing_id}`;
  return null;
};

export const isSinglesLocked = (track) => !!(track && track.singles_locked === true);
export const isSinglesForSale = (track) => !!(track && track.singles_for_sale === true);

export const SinglesLockBadge = ({
  track,
  variant = "row",
  className = "",
  testIdSuffix = "",
}) => {
  if (!track || !isSinglesForSale(track)) return null;
  const locked = isSinglesLocked(track);
  const price = fmtUsd(track.singles_price_usd);
  const buyPath = resolveBuyPath(track);
  const listingId = track.singles_listing_id || "";
  const tid = testIdSuffix ? `-${testIdSuffix}` : "";

  // ROW — inline pill for compact list rows.
  if (variant === "row") {
    return (
      <div
        className={`inline-flex items-center gap-1.5 flex-wrap ${className}`}
        data-testid={`singles-lock-row${tid}`}
      >
        <span
          className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-emerald-500/15 border border-emerald-400/40 text-emerald-200"
          data-testid={`singles-for-sale-badge${tid}`}
        >
          For Sale{price ? ` · ${price}` : ""}
        </span>
        {locked && (
          <span
            className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/10 border border-amber-400/40 text-amber-200"
            data-testid={`singles-locked-badge${tid}`}
            title="Preview only — buy the track to unlock the full version."
          >
            🔒 Preview
          </span>
        )}
        {buyPath && (
          <Link
            to={buyPath}
            onClick={(e) => e.stopPropagation()}
            className="px-2 py-0.5 rounded-full text-[10px] font-black bg-gradient-to-r from-emerald-500 to-cyan-500 text-black hover:from-emerald-400 hover:to-cyan-400 shadow-sm"
            data-testid={`singles-buy-btn${tid}`}
            data-listing-id={listingId}
          >
            Buy Track
          </Link>
        )}
      </div>
    );
  }

  // CARD — overlay chip for cover-art cards (Discovery / Charts rails).
  if (variant === "card") {
    return (
      <div
        className={`flex items-center gap-1 ${className}`}
        data-testid={`singles-lock-card${tid}`}
      >
        <span
          className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-emerald-500/90 text-black shadow"
          data-testid={`singles-for-sale-badge${tid}`}
        >
          For Sale{price ? ` · ${price}` : ""}
        </span>
        {locked && (
          <span
            className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-black/70 text-amber-200 border border-amber-400/50"
            data-testid={`singles-locked-badge${tid}`}
            title="Preview only"
          >
            🔒
          </span>
        )}
      </div>
    );
  }

  // HERO — Track Detail block.
  return (
    <div
      className={`rounded-xl border border-emerald-400/40 bg-gradient-to-br from-emerald-500/10 via-cyan-500/10 to-purple-500/10 p-4 ${className}`}
      data-testid={`singles-lock-hero${tid}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-emerald-500/20 border border-emerald-400/50 text-emerald-100"
          data-testid={`singles-for-sale-badge${tid}`}
        >
          For Sale
        </span>
        {price && (
          <span
            className="text-lg font-black text-emerald-200"
            data-testid={`singles-price${tid}`}
          >
            {price}
          </span>
        )}
        {locked && (
          <span
            className="px-2 py-1 rounded-full text-[10px] font-bold bg-amber-500/15 border border-amber-400/40 text-amber-200"
            data-testid={`singles-locked-badge${tid}`}
          >
            🔒 Preview only — full track locked
          </span>
        )}
      </div>
      <p className="mt-2 text-xs text-emerald-100/80">
        {locked
          ? "You're hearing a short preview. Buy the track for the full-length personal download."
          : "You own the full-length download for this single."}
      </p>
      {buyPath && locked && (
        <div className="mt-3">
          <Link
            to={buyPath}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-black bg-gradient-to-r from-emerald-500 to-cyan-500 text-black hover:from-emerald-400 hover:to-cyan-400 shadow-lg shadow-emerald-500/20"
            data-testid={`singles-buy-btn${tid}`}
            data-listing-id={listingId}
          >
            🛒 Buy Track{price ? ` · ${price}` : ""}
          </Link>
        </div>
      )}
    </div>
  );
};

export default SinglesLockBadge;
